import type { PrizeRow, MainEventPrizePayload } from '@/types/main-event-prize.types';

// a row is persisted only if it carries real prize data: positive coins, or a named item.
// blank rows (e.g. a freshly added tier left at 0 coins) are dropped silently — they were never filled in.
const isSavedRow = (p: PrizeRow): boolean =>
  (p.prizeType === 'COINS' && p.coinAmount > 0) ||
  (p.prizeType === 'ITEM' && p.itemName.trim().length > 0);

// validates prize rows before save. returns the first user-facing error message, or null when valid.
// item prizes must be named (otherwise they were being silently discarded), and every saved prize
// must claim a unique placement (the DB enforces @@unique([mainEventId, placement])).
export function validatePrizeRows(rows: PrizeRow[]): string | null {
  for (const p of rows) {
    if (p.prizeType === 'ITEM' && p.itemName.trim().length === 0) {
      return `Prize for placement ${p.placement} needs an item name (or remove the row).`;
    }
  }

  const seen = new Set<number>();
  for (const p of rows.filter(isSavedRow)) {
    if (seen.has(p.placement)) {
      return `Two prizes share placement ${p.placement} — each placement must be unique.`;
    }
    seen.add(p.placement);
  }

  return null;
}

// maps prize rows to the API payload, dropping blank rows. run validatePrizeRows first so that
// named item rows are guaranteed to survive (the previous inline filter dropped any item without a name).
export function buildPrizesPayload(rows: PrizeRow[]): MainEventPrizePayload[] {
  return rows.filter(isSavedRow).map((p) => ({
    placement: p.placement,
    prizeType: p.prizeType,
    coinAmount: p.prizeType === 'COINS' ? p.coinAmount : 0,
    itemName: p.prizeType === 'ITEM' ? p.itemName.trim() : null,
    itemImageUrl: p.prizeType === 'ITEM' && p.itemImageUrl.trim() ? p.itemImageUrl.trim() : null,
  }));
}
